import { PropsWithChildren } from 'react';
import { createQuery } from 'react-query-kit';

import { createApi, STALE_TIME } from '@blockworks/platform/api';
import { createSafeContext } from '@blockworks/ui/utils';

import { RESPONSE_HEADER_NAME } from '@/api/csrf/options';

const [SyncCtxProvider, useSyncToken, SyncContext] = createSafeContext({
    name: 'SyncContext',
    strict: false,
    hookName: 'useSyncToken',
    providerName: 'SyncProvider',
    defaultValue: '',
});

// Grab headers from the root HTML request of the root - this is used to sync session state for CSRF protection
// This is specifically required to work around SSG and SSR differences with App.getInitialProps preventing reading
// the initial headers of a given entry points request.

const syncApi = createApi(build => ({
    get: {
        synchronize: build({
            baseUrl: 'same-origin',
            cachePrefix: 'sync',
            queryFn: async (_, { baseFetch }) => {
                const res = await baseFetch({
                    originalResponse: true,
                    url: `/api/ticker`, // Just hijack the ticker endpoint for this purpose because NEXT JS IS A NIGHTMARE
                    method: build.Method.Get,
                });

                return res.headers.get(RESPONSE_HEADER_NAME);
            },
        }),
    },
}));

const useSyncQuery = createQuery({
    queryKey: [syncApi.get.synchronize.cachePrefix],
    fetcher: syncApi.get.synchronize,
});

const sync = { use: useSyncQuery };

const SyncProvider = ({ children }: PropsWithChildren) => {
    const syncQuery = sync.use({ refetchOnWindowFocus: true, staleTime: STALE_TIME.$3Minutes });
    const providedValue = syncQuery.data! ?? '';
    return <SyncCtxProvider value={providedValue}>{children}</SyncCtxProvider>;
};

export { SyncContext, SyncProvider, useSyncToken };
